import { describePasswordViolation } from './password-policy.js';
/**
 * Organizer onboarding — the setup wizard's shape, its field rules and its copy
 * (`ORG-001`…`ORG-008`).
 *
 * ## Why this lives in `contracts` rather than in the API or the SPA
 *
 * The same three parties need the same answers and must not disagree: the wizard renders a step
 * and validates it, the API enforces the same rules on the way in, and the tests assert them.
 * `apps/organizer` runs `tsx --test '__tests__/**\/*.test.ts'` — plain `.ts`, no DOM renderer — so
 * anything an acceptance criterion quotes has to live outside a `.tsx` file to be testable at all.
 * That is why the copy is here and not inline in a component.
 *
 * ## Two steps in the flow, four in the vocabulary
 *
 * **Read this before any count below.** Since 2026-09-12 the wizard somebody walks is
 * **account → profile, two steps** — {@link ORGANIZER_SETUP_FLOW_STEP_IDS}, and the only list a
 * counter, a progress bar or a route table may derive from. {@link ORGANIZER_SETUP_STEP_IDS} is
 * still four, because `plan` and `team` remain a *vocabulary*: the API records both among an
 * organization's completed steps, the copy for both is kept, and Phase 2 puts the plan step back
 * with one switch ({@link PLANS_ARE_ENABLED}).
 *
 * Every "four" in the section below is the **prototype's**, transcribed. Nothing in it describes
 * what the product does now.
 *
 * ## The prototype's four steps, and its counter disagreeing with itself
 *
 * `Organizer Setup Desktop.dc.html` has ten screens. Six of them are its flow:
 *
 * ```
 * s2  "Step 1 of 4 · Choose a plan"   →  plan
 * s3  "Step 2 of 4"                   →  account          (legal modal lives here)
 * s4  "Step 2 of 4"                   →  check your email — an interstitial, not a step
 * s5  "Step 3 of 4"                   →  profile
 * s6  "Step 3 of 4"                   →  organization check — an interstitial, not a step
 * s7  "Step 4 of 4"                   →  team
 * ```
 *
 * Two screens share "Step 3 of 4" and two share "Step 2 of 4", which is what `ORG-001` AC-2 means
 * by the counter and the progress bar disagreeing. The specification's corrections table settles
 * it: **plan, account, profile, team is four**, and the interstitials inside steps 2 and 3 are not
 * extra steps. {@link ORGANIZER_SETUP_STEP_IDS} is that list — the *vocabulary*, per the section
 * above, not the flow. Every indicator derives both its position and its total from
 * {@link ORGANIZER_SETUP_FLOW_STEP_IDS} rather than writing either down; a literal is how the two
 * drift back apart, and two of the four literals here are now one step apart from what a person
 * sees.
 *
 * `s1` is the entry page and `s9`/`s10` are sign-in and recovery, which the `users` intake owns.
 *
 * ## The desktop and mobile prototypes disagree about step 1
 *
 * `Organizer Setup Mobile.dc.html`'s `m2` is not the plan step — it asks "First — which sounds like
 * you?" (hosting for an organization versus hosting alone) and the plan never appears. `ORG-001`
 * AC-3 says step 1 records the plan, so the desktop reading wins on behaviour and the mobile
 * question is not built: no story covers it, and inventing one would be scope nobody asked for.
 * Raised on the pull request rather than resolved silently, per `building-from-designs.md`.
 *
 * ## The plan is data, not an entitlement
 *
 * {@link ORGANIZER_PLANS} carries what the plan cards render and nothing else. No code in this
 * repository reads a plan to decide whether an organizer may do something, and `ORG-001` AC-4 is
 * the assertion that keeps it that way. The prices are display copy for a step that takes no
 * payment details; subscription collection is Phase 2.
 */
/* ============================================================================
   Steps
   ============================================================================ */
/**
 * Every step id this codebase knows, **including one the flow no longer walks**.
 *
 * This is the *vocabulary*, not the flow. `plan` stays in it because the plan step's code is kept
 * — Sumit, 2026-09-11: *"keep the code for future when we enable the plans in phase 2"* — and a
 * great deal of that code is typed on this union: `STEP_CHROME` is a total `Record` over it, the
 * API still stores `'plan'` among an organization's completed steps, and `stepIdFromPath` still
 * returns it for `/setup/plan`. Deleting the member would not remove the plan step; it would stop
 * the kept code compiling.
 *
 * **What decides the counter is {@link ORGANIZER_SETUP_FLOW_STEP_IDS}.**
 */
export const ORGANIZER_SETUP_STEP_IDS = ['plan', 'account', 'profile', 'team'];
/**
 * The steps setup actually walks: **account, then public profile**. Two.
 *
 * Sumit ruled on 2026-09-11 that there are no plans in MVP and the picker leaves sign-up —
 * *"ya, because we don't have the plans in MVP. keep the code for future when we enable the plans
 * in phase 2"*. That **departs from `ORG-ACC-002`**, which makes the plan step 1 of 4, and the
 * departure is confirmed: `ORG-001` AC-3 is struck to `PLT-013`'s Phase 2 register and `ORG-001`
 * AC-2 is reworded — to three steps that day, and to two the next, when the team step went the
 * same way (below).
 *
 * Every position, every total and the resume logic derive from **this** list, so the counter and
 * the step count cannot disagree — which is `ORG-001` AC-2's own requirement, and the thing the
 * prototype gets wrong in a new way now that the picker is gone: its phase maths still counts the
 * step, so its first form screen reads *"Step 2 of 4"* with two of four segments filled
 * (`handoff-corrections.md` #134).
 *
 * ## The team step left too, on 2026-09-12
 *
 * Sumit, after walking the seeded flow: *"if we are not letting them setup team during setup, no
 * point showing that screen, we can remove it and take them to the finish screen."*
 *
 * He is right about what it was. Inviting is `ORG-010` and lives at Settings -> Team; `ORG-007`
 * AC-4 only ever asked this step for a heading, a sub-head and a way past, so the screen's whole
 * content was a note saying it could not do the thing it was named after. A step that exists to
 * be skipped is a step.
 *
 * Same treatment as the plan step, and by the same mechanism: **out of this list, everything else
 * kept**. `TeamStepPanel`, `STEP_CHROME.team`, the step's copy and `/setup/team` all remain, so
 * inviting during setup is one entry here away if it is ever wanted. `ORG-007` AC-4 is struck to
 * `PLT-013`'s register with its words intact.
 *
 * Two lists rather than a filter, so that adding either step back is one entry here.
 */
export const ORGANIZER_SETUP_FLOW_STEP_IDS = ['account', 'profile'];
/**
 * **The one switch that turns plans back on.**
 *
 * Sumit ruled twice, a day apart, and the two rulings are the same decision seen from either end
 * of the product:
 *
 * - 2026-09-11, on sign-up: *"ya, because we don't have the plans in MVP. keep the code for future
 *   when we enable the plans in phase 2"*.
 * - 2026-09-12, on the organizer's own Settings screen, asked whether it should keep showing every
 *   organizer *"Curate"* and *"300 free registrations"* now that nobody chooses either:
 *   *"yes hide it"*.
 *
 * So **no plan is named on any surface a person can reach**, and none of the code that names one
 * is deleted. This constant is what a Phase 2 engineer flips, and the list below is what flipping
 * it turns back on:
 *
 * | Reads it | What comes back |
 * | --- | --- |
 * | `apps/organizer` · `plan-and-payments-panel.tsx` | The plan name, the monthly allowance, the usage against it and the overage row on Settings → Plan & payments (`FIN-011` AC-6, struck to `PLT-013`) |
 * | `apps/organizer` · `setup-wizard-page.tsx`, via {@link ORGANIZER_SETUP_FLOW_STEP_IDS} | The plan step in setup (`ORG-001` AC-3, struck to `PLT-013`) |
 * | `apps/organizer` · `auth/public-paths.ts` · `publicSetupPaths` | The `/setup/plan` route itself — mounted in `App.tsx` and public to the redirector, or neither |
 * | `apps/api` · `organizers/onboarding-wizard-shell.ts` | `plans` on `GET /onboarding/wizard`, which is where the cards' names and prices come from |
 *
 * **The flow list is deliberately not derived from this.** Deriving it would widen its type from a
 * literal tuple to a union of two, and `OrganizerSetupStepId`, `STEP_CHROME`'s totality and
 * `stepIdFromPath`'s return type all rest on that tuple. The two are held together by a test
 * instead — `constants.test.ts` asserts that while this is `false` the flow excludes `'plan'` —
 * so they cannot drift apart silently even though they are written down twice.
 *
 * What it does **not** gate is anything behavioural. There is no entitlement, no cap and no
 * feature flag keyed on a plan anywhere in this repository (`ORG-001` AC-4, audited 2026-09-12);
 * this hides *names and figures*, which is all a plan is in MVP.
 */
export const PLANS_ARE_ENABLED = false;
/**
 * Every step's copy, **including the plan step's**, which is kept and unreachable.
 *
 * The headings are verbatim requirements, not placeholder copy — `ORG-005` AC-1 and `ORG-007` AC-4
 * each name one character for character, which is why they are constants with tests on them rather
 * than strings in a component.
 *
 * The **flow** is {@link ORGANIZER_SETUP_FLOW_STEPS}. This list is what a screen reaches for when
 * it needs a step's copy by id, the plan step included — its panel is kept and compiles, and
 * {@link PLANS_ARE_ENABLED} decides whether anything mounts a route to it.
 */
export const ORGANIZER_SETUP_STEPS = [
    {
        id: 'plan',
        heading: 'First — pick a plan',
        subheading: 'Start free and move up whenever you outgrow it. Nothing here is locked in — you can change plan from Settings at any time.',
        label: 'Choose a plan',
        skippable: false,
    },
    {
        id: 'account',
        heading: 'Create your account',
        subheading: 'One login for everything you do on H2HSpark — hosting and attending.',
        label: 'Your account',
        skippable: false,
    },
    {
        id: 'profile',
        // `ORG-005` AC-1, verbatim.
        heading: 'How members will know you',
        subheading: 'This is your public page. Members follow it to hear about your next event.',
        label: 'Your public profile',
        skippable: false,
    },
    {
        id: 'team',
        // `ORG-007` AC-4, verbatim. The dash is an en dash, as the specification writes it.
        heading: 'Who else helps you run events?',
        subheading: 'Invite them now or on event day — door volunteers can be added in seconds from the check-in screen.',
        label: 'Your team, if you have one',
        skippable: true,
    },
];
/** The steps the flow walks, in order, with their copy. Derived, so the two lists cannot drift. */
export const ORGANIZER_SETUP_FLOW_STEPS = ORGANIZER_SETUP_FLOW_STEP_IDS.map((id) => ORGANIZER_SETUP_STEPS.find((step) => step.id === id));
/**
 * **Two.** Derived, never written down — see this module's header, and `ORG-001` AC-2's *"the
 * indicator and the step count agree"*, which is the criterion the prototype breaks by counting
 * two steps it no longer draws.
 */
export const ORGANIZER_SETUP_STEP_COUNT = ORGANIZER_SETUP_FLOW_STEPS.length;
/** The step at a 1-based position in the flow, or `null` when the position is off the end. */
export function organizerSetupStepAt(position) {
    return ORGANIZER_SETUP_FLOW_STEPS[position - 1] ?? null;
}
/**
 * A step's 1-based position in the flow, for an indicator that must agree with the count.
 *
 * **`0` for a step outside the flow** — the plan step and the team step, both kept as vocabulary.
 * Zero rather than a throw because their panels still exist and an indicator reading "Step 0 of 2"
 * is a visible, harmless oddity where a throw would be a white page. It is also falsy, which is
 * what the chrome tests for before drawing a bar at all.
 */
export function organizerSetupStepPosition(id) {
    return ORGANIZER_SETUP_FLOW_STEP_IDS.indexOf(id) + 1;
}
/**
 * Where signing in with setup incomplete lands (`ORG-007` AC-3).
 *
 * The first step that is not finished, or `null` when they all are — and `null` means the console,
 * not step 1. Returning the first *incomplete* step rather than "the one after the last complete
 * one" is deliberate: a person who skipped the team step and later completed the profile should
 * resume at team, not be walked back through a step they already did.
 */
export function organizerResumeStepFor(state) {
    const done = new Set(state.completedStepIds);
    // Over the **flow**, not the vocabulary. The API still records `'plan'` among an organization's
    // completed steps — the account request writes both, and rewriting seven years of stored rows to
    // drop one string is not a thing to do for a counter — so resuming must neither require it nor
    // be confused by it. An organization created before or after the picker left resumes the same.
    return ORGANIZER_SETUP_FLOW_STEP_IDS.find((id) => !done.has(id)) ?? null;
}
/**
 * Whether setup is finished.
 *
 * **This is not a lock.** `ORG-007` AC-6: an organization with setup incomplete is fully usable,
 * and the state exists so the flow knows where to resume rather than to withhold the console.
 * Nothing in this repository gates a console route on it, and a test asserts as much.
 */
export function organizerSetupIsComplete(state) {
    return organizerResumeStepFor(state) === null;
}
/* ============================================================================
   Step 1 — the plan (`ORG-001`)
   ============================================================================ */
export const ORGANIZER_PLAN_IDS = [
    'spark',
    'connect',
    'curate',
    'community-pro',
    'enterprise',
];
/**
 * The one plan that is not chosen on step 1.
 *
 * `s2` draws four cards in `grid-template-columns:repeat(4,minmax(0,1fr))` and then puts
 * Enterprise **after** the grid closes, as a full-width `--purple-800` banner whose only control
 * is "Talk to us". It carries no price, no selected state and no card click — which is the
 * prototype saying, in layout, that Enterprise is sales-led and cannot be self-served onto.
 *
 * Our step 1 rendered it as a fifth clickable card, so it could become the wizard's `planId` and
 * be posted with the account. {@link isOrganizerSelectablePlanId} is the rule stated once;
 * {@link describeAccountStepViolations} enforces it on the way in, so the screen and the server
 * agree rather than the screen being the only guard.
 *
 * It stays in {@link ORGANIZER_PLAN_IDS}: an administrator can still assign it from
 * `PATCH /api/organizations/:id/plan`, which is how a sales-led plan is meant to arrive.
 */
export const ORGANIZER_ENTERPRISE_PLAN_ID = 'enterprise';
/**
 * The plan cards, transcribed from `s2`.
 *
 * Display data. See this module's header for why nothing reads it to make a decision.
 */
export const ORGANIZER_PLANS = [
    {
        id: 'spark',
        name: 'Spark',
        monthlyPriceUsd: 0,
        annualPriceUsd: 0,
        summary: 'For first-time and very small organizers.',
        registrationAllowance: '25 registrations a month',
        features: [
            'Organizer profile',
            'One active event',
            'Basic registration and check-in',
            'Standard attendee profiles',
            'H2HSpark branding on your page',
        ],
        // The only tier `s2` draws with `minus` rows. They are what Connect adds.
        unavailableFeatures: [
            'Curated connection recommendations',
            'Attendee-list export',
            'Limited email communication',
        ],
        highlighted: false,
        callToAction: 'Start free',
    },
    {
        id: 'connect',
        name: 'Connect',
        monthlyPriceUsd: 39,
        annualPriceUsd: 390,
        summary: 'For small networking groups, chambers, consultants and community organizations.',
        registrationAllowance: '100 registrations a month',
        features: [
            'Unlimited event listings',
            'Custom registration questions',
            'Attendee exports',
            'Waitlist',
            'Automated confirmations and reminders',
            'Basic post-event follow-up',
            'Basic sponsor recognition',
            'Three team users',
        ],
        unavailableFeatures: [],
        highlighted: false,
        callToAction: 'Choose Connect',
    },
    {
        id: 'curate',
        name: 'Curate',
        monthlyPriceUsd: 99,
        annualPriceUsd: 990,
        summary: 'Everything in Connect, plus the matching and follow-up H2HSpark is built for.',
        registrationAllowance: '300 registrations a month',
        features: [
            'Pre-event recommended connections',
            '“Why you should meet” descriptions',
            'In-event QR and tap-to-connect',
            'Personalized post-event follow-up',
            'Relationship nudges',
            'Interest and objective reporting',
            'Sponsor analytics · multiple series',
            'Ten team users · reduced ticketing fee',
        ],
        unavailableFeatures: [],
        highlighted: true,
        callToAction: 'Choose Curate',
    },
    {
        id: 'community-pro',
        name: 'Community Pro',
        monthlyPriceUsd: 249,
        annualPriceUsd: 2490,
        summary: 'For chambers, associations, coworking spaces, accelerators and larger nonprofits.',
        registrationAllowance: '1,000 registrations a month',
        features: [
            'Multiple communities or chapters',
            'Advanced reporting',
            'Sponsor and exhibitor tools',
            'White-label options',
            'CRM integration',
            'Priority support',
            'Custom onboarding',
            'Lower ticketing fees',
        ],
        unavailableFeatures: [],
        highlighted: false,
        callToAction: 'Choose Community Pro',
    },
    {
        id: ORGANIZER_ENTERPRISE_PLAN_ID,
        name: 'Enterprise',
        monthlyPriceUsd: null,
        annualPriceUsd: null,
        summary: 'For universities, corporations, statewide organizations and conference producers. Custom pricing.',
        registrationAllowance: null,
        features: [],
        unavailableFeatures: [],
        highlighted: false,
        callToAction: 'Talk to us',
    },
];
export function isOrganizerPlanId(candidate) {
    return ORGANIZER_PLAN_IDS.includes(candidate);
}
/**
 * Whether an organizer may put themselves on this plan during setup.
 *
 * Every plan but Enterprise — see {@link ORGANIZER_ENTERPRISE_PLAN_ID} for why the prototype's
 * own layout is the argument.
 */
export function isOrganizerSelectablePlanId(candidate) {
    return isOrganizerPlanId(candidate) && candidate !== ORGANIZER_ENTERPRISE_PLAN_ID;
}
/**
 * **The plan an organizer is created on through sign-up.**
 *
 * Sumit, 2026-09-12: *"one change in org signup, let's change the default plan for spark."*
 *
 * ## Why this is now a literal, when it used to be derived
 *
 * It read `ORGANIZER_PLANS.find((plan) => plan.highlighted)` — the badged card — on the argument
 * that *"the recommended plan and the default cannot become two different cards"*. That argument
 * held while the plan step was a picker a person opened: the card the wizard opens on and the card
 * the prototype recommends should be the same card, and `s2` badges Curate.
 *
 * Two things ended it. {@link PLANS_ARE_ENABLED} took the picker out of the flow, so this stopped
 * being *which card is preselected* and became *the plan every new organizer is created on with no
 * screen in between*. And the ruling above makes the two facts deliberately different: the
 * prototype still recommends Curate, and sign-up now creates on Spark. A derivation that forced
 * them together would have to be undone by moving the badge, which would change the recommendation
 * nobody asked to change.
 *
 * So it is written, and the badge is left where the prototype puts it.
 *
 * ## What this changes today: nothing anybody can see
 *
 * Nothing in this repository gates on a plan id — no permission, no feature flag, no cap, no
 * entitlement. The one read that is more than display is `PLAN_REGISTRATION_ALLOWANCES[planId]`
 * feeding the overage meter, which never blocks and whose rate is a hard zero
 * (`FOUNDING_COHORT_OVERAGE_RATE_CENTS`). And both plan surfaces — the setup step and Settings →
 * Plan & payments — are hidden behind {@link PLANS_ARE_ENABLED}.
 *
 * ## What it will change in phase 2, which is the part to read before switching plans on
 *
 * **Spark's own card excludes the product's core feature.** Its `unavailableFeatures` are
 * *"Curated connection recommendations"*, *"Attendee-list export"* and *"Limited email
 * communication"*, and its features list says *"One active event"* with an allowance of 25
 * registrations a month. Curate is the tier whose summary is *"the matching and follow-up H2HSpark
 * is built for"*.
 *
 * So every organizer created between this ruling and the day plans start gating anything lands on
 * the tier that excludes curated matching and caps them at one active event. When plans are turned
 * on, somebody has to decide one of: migrate that cohort, change what Spark includes, or accept
 * it. Recorded in `ORG-001`'s Notes as well, so the decision is visible from the story rather than
 * only from here.
 *
 * ## Not to be confused with `FOUNDING_COHORT_PLAN_ID`
 *
 * That one is `apps/api`'s, it is also `'curate'`, and it is a **different fact**: `FIN-003` AC-2
 * says every founding-cohort organizer is assigned Curate, and a founding-cohort organizer is one
 * H2HSpark provisioned directly with no plan assignment at all. It is unchanged by this ruling and
 * changing it would contradict a stamped criterion. Its own docblock predicted this collision.
 */
export const ORGANIZER_DEFAULT_PLAN_ID = 'spark';
/**
 * Every field name the plan step is allowed to submit.
 *
 * `ORG-001` AC-3 forbids payment details "of any kind" at this step, and the corresponding test
 * asserts against this list rather than against a screenshot. A card number arriving here would
 * have to be added to this array first, which is the point: it makes the prohibited thing a
 * reviewed diff instead of an oversight.
 */
export const ORGANIZER_PLAN_STEP_FIELDS = ['planId', 'billingPeriod'];
export const ORGANIZER_BILLING_PERIODS = ['monthly', 'annual'];
/* ============================================================================
   Step 2 — the account (`ORG-002`)
   ============================================================================ */
/**
 * The terms checkbox, verbatim (`ORG-002` AC-2).
 *
 * It is not placeholder copy. The second sentence is the member-data boundary stated to the
 * organizer in their own words — the same rule `security-invariants#member-data-boundary`
 * enforces — so rewording it would leave the code enforcing something nobody agreed to.
 *
 * The apostrophes are typographic, as the specification writes them.
 */
export const ORGANIZER_TERMS_ACCEPTANCE_TEXT = "I agree to the organizer terms and the privacy policy. We'll never share a member's personal data with you beyond what running your event requires.";
/**
 * Which documents were accepted.
 *
 * `ORG-002` AC-4: "the document version and a timestamp, not merely as a boolean that passed
 * validation". The terms will change and the question later is which version this organizer
 * accepted, which a boolean cannot answer. The dates are the prototype's own "Last updated" lines
 * on the legal modal.
 */
export const ORGANIZER_TERMS_VERSION = '2026-06-02';
export const ORGANIZER_PRIVACY_VERSION = '2026-07-14';
export const ORGANIZER_ACCOUNT_FIELD_LIMITS = {
    firstName: 60,
    lastName: 60,
    organizationName: 120,
    emailAddress: 254,
};
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Every reason this submission cannot be accepted, or an empty list.
 *
 * One pass naming every unmet rule rather than one rejection at a time, for the same reason
 * {@link describePasswordViolation} does it: somebody filling in a form should not have to
 * discover the requirements one round trip at a time.
 *
 * **The password rule is not restated here.** `describePasswordViolation` is the only definition,
 * it mirrors the Cognito pool, and a second one drifted twice this week — the second time in the
 * kinder direction, which recreated a live defect because the pool then refused a password the
 * screen had called acceptable. A validator may be stricter than the pool; it may never be more
 * permissive. The prototype's own "12+ characters, one number" hint is exactly that mistake in
 * copy form (stricter on length, silent about symbols) and is deliberately not transcribed —
 * screens render `PASSWORD_RULES` instead.
 */
export function describeAccountStepViolations(input) {
    const refusals = [];
    const requireText = (field, value, label) => {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            refusals.push({ field, message: `${label} is required.` });
            return;
        }
        const limit = ORGANIZER_ACCOUNT_FIELD_LIMITS[field];
        if (trimmed.length > limit) {
            refusals.push({ field, message: `${label} must be ${String(limit)} characters or fewer.` });
        }
    };
    requireText('firstName', input.firstName, 'First name');
    requireText('lastName', input.lastName, 'Last name');
    requireText('emailAddress', input.emailAddress, 'Work email');
    requireText('organizationName', input.organizationName, 'Organization name');
    const email = input.emailAddress.trim();
    if (email.length > 0 && !EMAIL_SHAPE.test(email)) {
        refusals.push({ field: 'emailAddress', message: 'Enter an email address.' });
    }
    const passwordViolation = describePasswordViolation(input.password);
    if (passwordViolation !== null) {
        refusals.push({ field: 'password', message: passwordViolation });
    }
    if (!input.termsAccepted) {
        refusals.push({
            field: 'termsAccepted',
            message: 'Agree to the organizer terms and the privacy policy to continue.',
        });
    }
    // Not `isOrganizerPlanId`: Enterprise is a real plan id an administrator can assign, and is not
    // one an organizer can put themselves on during setup. The screen no longer offers it — this is
    // the half that holds when the screen is not the thing making the request.
    if (!isOrganizerSelectablePlanId(input.planId)) {
        refusals.push({ field: 'planId', message: 'Choose a plan.' });
    }
    return refusals;
}
/**
 * The display name the person is created with.
 *
 * One place, because the account step collects two name fields and everything downstream — the
 * console's account menu, the team roster's "— you" row, the mailed verification link — reads one.
 */
export function organizerDisplayName(firstName, lastName) {
    return `${firstName.trim()} ${lastName.trim()}`.trim();
}
/* ============================================================================
   Email verification (`ORG-004`)
   ============================================================================ */
/**
 * Fourteen days, from `SYS-013`'s platform rule.
 *
 * The prototype's `s4` says 24 hours in two places. `ORG-004`'s Notes record that as one of the two
 * things the prototype gets wrong and the specification corrects; this constant is the corrected
 * value and the screens read it rather than restating either number.
 */
export const ORGANIZER_EMAIL_VERIFICATION_LIFETIME_DAYS = 14;
/**
 * The verification interstitial and the console banner, verbatim where an AC quotes them.
 *
 * `s4` offers "I've confirmed — continue", which is a self-attestation that advances the flow as
 * though the address were verified. `ORG-004`'s Notes rule it out explicitly: the confirmed state
 * arrives only from the emailed link, and the action is a plain "Continue to your profile". So
 * {@link ORGANIZER_VERIFICATION_COPY.continueAction} is that, and no key here asserts confirmation.
 */
export const ORGANIZER_VERIFICATION_COPY = {
    heading: 'Check your email',
    sentTo: 'We sent a confirmation link to',
    /** Replaces the prototype's "I've confirmed — continue". */
    continueAction: 'Continue to your profile',
    keepGoingTitle: 'You can keep setting up while you wait',
    keepGoingBody: 'Your profile and team can be filled in now. Confirmation is only required before you publish your first event.',
    resendAction: 'Resend the link',
    /** `ORG-004` AC-5, verbatim. */
    resendConfirmation: 'Sent. Check your inbox - and your spam folder.',
    changeAddressAction: 'Use a different email',
    /** `ORG-004` AC-4, verbatim. */
    unconfirmedBanner: "Your email isn't confirmed yet. You can look around, but publishing is on hold until it is.",
};
/* ============================================================================
   Lost access to the account email (`ORG-008`)
   ============================================================================ */
/**
 * `ORG-008` AC-2, verbatim.
 *
 * Rendered as one sentence with "Talk to us" as the link, which is how `s10` draws it. The route
 * behind it is `ORG-012`'s administrator-authorised ownership move, not an email change — there is
 * deliberately no self-serve way to change the address on an account nobody can read, because
 * every check such a route could make is one an attacker also passes (`ORG-008` AC-1).
 */
export const ORGANIZER_LOST_EMAIL_COPY = "Lost access to the email on the account? Talk to us - we'll verify you another way and hand ownership over.";
/**
 * What the confirmation step says before anything is written (`ORG-003` AC-3).
 *
 * AC-3 is "the step tells the person what is about to happen to their existing account **before
 * they commit to it**", so these sentences are the criterion rather than decoration, and the two
 * halves are stated separately on purpose:
 *
 *   - {@link keeps} names what survives. The story's whole premise is that the person who
 *     registered for last month's event and the person running next month's are the same person,
 *     and the fear this screen answers is that hosting overwrites the member.
 *   - {@link adds} names what appears. Nothing on this list is a change to anything that already
 *     exists — an organization, an owning membership, and console access are all new rows.
 *
 * They are constants rather than markup so the screen and the test that asserts AC-3 read one
 * source; a sentence transcribed into a component is a sentence a test can only re-transcribe.
 */
export const ORGANIZER_HOSTING_ADDITION_COPY = {
    heading: 'Add hosting to your account',
    /** Rendered with the account's address beside it — see {@link OrganizerHostingPreview}. */
    signedInAs: "You're signed in as",
    keepsHeading: 'What stays exactly as it is',
    keeps: [
        'Your account. Adding hosting does not create a second one, and you keep signing in the same way.',
        'The events you have registered for, past and future, and your tickets.',
        'Your member profile, your matching preferences and your connections.',
    ],
    addsHeading: 'What gets added',
    adds: [
        'An organization, which you own.',
        'The organizer console, alongside the member app you already use.',
    ],
    /**
     * The one thing on this screen that is a change rather than an addition, said plainly rather
     * than left for somebody to discover on a bill.
     */
    planNote: 'Your plan choice is recorded against the new organization, not against your membership.',
    confirmAction: 'Add hosting',
    /** Leaves for the member app (`ORG-003` AC-10). Not the console: it would send them back here. */
    cancelAction: 'Not now',
    /**
     * For somebody a colleague invited, who signed in before opening the invitation (`ORG-003`
     * AC-10). The console sends everyone without an organization to this step, so without this line
     * they would be offered an organization of their own when they came to join someone else's.
     */
    invitedElsewhere: "Joining someone else's organization? Open the invitation they emailed you instead.",
    /** Shown instead of the confirm control when {@link OrganizerHostingPreview.alreadyHosts}. */
    alreadyHosts: 'This account already hosts. Open the console to work on the organization you have.',
    alreadyHostsAction: 'Open the console',
};
/**
 * Every reason an addition cannot be accepted, or an empty list (`ORG-003` AC-1).
 *
 * The organization-name rule is {@link describeAccountStepViolations}' rule, reached through the
 * same {@link ORGANIZER_ACCOUNT_FIELD_LIMITS} entry rather than restated — the two paths create
 * the same row and a name one accepted and the other refused would be a difference with no
 * product meaning behind it.
 */
export function describeHostingAdditionViolations(input) {
    const refusals = [];
    const organizationName = input.organizationName.trim();
    if (organizationName.length === 0) {
        refusals.push({ field: 'organizationName', message: 'Organization name is required.' });
    }
    else if (organizationName.length > ORGANIZER_ACCOUNT_FIELD_LIMITS.organizationName) {
        refusals.push({
            field: 'organizationName',
            message: `Organization name must be ${String(ORGANIZER_ACCOUNT_FIELD_LIMITS.organizationName)} characters or fewer.`,
        });
    }
    if (!input.termsAccepted) {
        refusals.push({
            field: 'termsAccepted',
            message: 'Agree to the organizer terms and the privacy policy to continue.',
        });
    }
    // Enterprise again: a real plan, and not one anybody puts themselves on. Same rule as the
    // account step, for the same reason, through the same predicate.
    if (!isOrganizerSelectablePlanId(input.planId)) {
        refusals.push({ field: 'planId', message: 'Choose a plan.' });
    }
    return refusals;
}
//# sourceMappingURL=organizer-onboarding.js.map